/**
 * Opening a supply pod (02 §3.2, §8): he takes what he can carry, most useful first, and the
 * rest stays in the pod for a second visit. The only limit is weight - so Bunker Buster's
 * "+1 LAW capacity" (`mods.extraLaws`) is that many LAWs the limit does not count.
 */

import type { Ctx } from '../ctx';
import { takeQuestItem } from '../objectives/questItem';
import { RANGER, WEIGHT } from '../tuning';
import type { Ranger, SimMods } from '../types';
import { carriedWeight } from './movement';

/** Weight as the carry limit sees it, rare gear included: his `extraLaws` ride free. (Encumbrance feels every one.) */
export function limitWeight(r: Pick<Ranger, 'inventory' | 'gear'>, mods: Pick<SimMods, 'extraLaws'>): number {
  const free = Math.min(r.inventory.laws, Math.max(0, mods.extraLaws)) * WEIGHT.law;
  return carriedWeight(r.inventory, r.gear) - free;
}

export function openPod(ctx: Ctx, index: number): void {
  const w = ctx.world;
  const pod = w.insertion.pods.find((p) => p.index === index);
  if (!pod) return;
  const cap = RANGER.carryCap + w.mods.carryBonus;
  const inv = w.ranger.inventory;
  const take = (key: 'magazines' | 'grenades' | 'laws' | 'bombs' | 'firstAid'): void => {
    while (pod.contents[key] > 0) {
      inv[key]++;
      if (limitWeight(w.ranger, w.mods) > cap) {
        inv[key]--;
        return;
      }
      pod.contents[key]--;
    }
  };
  take('firstAid');
  take('magazines');
  take('bombs');
  take('laws');
  take('grenades');
  const c = pod.contents;
  if (c.magazines + c.grenades + c.laws + c.bombs + c.firstAid === 0) pod.state = 'emptied';
  ctx.events.push({ type: 'podOpened', index: pod.index });
  // OBJ: the mission's quest item rides in a named pod and comes out with the rest, whatever it weighs (10 §3).
  takeQuestItem(ctx, pod.index);
}
